import { createApiToken, getApiTokens, revokeApiToken, type ApiToken } from "@/api/user.service";
import { useNotification } from "@/context/NotificationContext";
import { ContentCopy, Delete } from "@mui/icons-material";
import { Alert, Box, Button, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { ConfirmModal } from "@/components/ConfirmModal";

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString() : "—");

// Gerencia os tokens de API de uma conta de serviço. Só admins chegam aqui.
export default function ApiTokensSection({ userId }: { userId: string }) {
  const { notify } = useNotification();
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [name, setName] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [created, setCreated] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState<ApiToken | null>(null);

  const load = async () => {
    try {
      setTokens(await getApiTokens(userId));
    } catch (e: any) {
      notify(e?.message || "Erro ao carregar tokens", "error");
    }
  };

  useEffect(() => { load(); }, [userId]);

  const handleCreate = async () => {
    try {
      const res = await createApiToken(userId, {
        name: name.trim(),
        // <input type="date"> dá só o dia; o token vale até o fim dele.
        expires_at: expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : undefined,
      });
      setCreated(res.token);
      setCopied(false);
      setName("");
      setExpiresAt("");
      load();
    } catch (e: any) {
      notify(e?.message || "Erro ao gerar token", "error");
    }
  };

  const handleCopy = async () => {
    if (!created) return;
    await navigator.clipboard.writeText(created);
    setCopied(true);
  };

  const handleRevoke = async () => {
    if (!revoking) return;
    try {
      await revokeApiToken(userId, revoking.id);
      notify("Token revogado!", "success");
      load();
    } catch (e: any) {
      notify(e?.message || "Erro ao revogar token", "error");
    } finally {
      setRevoking(null);
    }
  };

  return (
    <Stack spacing={3}>
      {created && (
        <Alert severity="warning" onClose={() => setCreated(null)}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Este token só será exibido uma vez. Copie-o agora.
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              fullWidth
              size="small"
              value={created}
              slotProps={{ input: { readOnly: true } }}
              sx={{ "& .MuiInputBase-input": { fontFamily: "monospace", fontSize: "0.85rem" } }}
            />
            <Tooltip title={copied ? "Copiado!" : "Copiar"}>
              <IconButton aria-label="Copiar token" onClick={handleCopy} color={copied ? "success" : "default"}>
                <ContentCopy />
              </IconButton>
            </Tooltip>
          </Stack>
        </Alert>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
        <TextField
          label="Nome do token"
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="ex.: n8n produção"
          fullWidth
        />
        <TextField
          label="Expira em"
          type="date"
          size="small"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          helperText="Opcional"
          sx={{ minWidth: 180 }}
        />
        <Button variant="contained" onClick={handleCreate} disabled={!name.trim()} sx={{ flexShrink: 0 }}>
          Gerar token
        </Button>
      </Stack>

      {tokens.length === 0 ? (
        <Typography variant="body2" sx={{ textAlign: "center", py: 4, color: "text.disabled" }}>
          Nenhum token gerado para esta conta de serviço.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {tokens.map((t) => (
            <Box
              key={t.id}
              sx={{ display: "flex", alignItems: "center", gap: 2, p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}
            >
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600}>{t.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Criado: {formatDate(t.created_at)} · Último uso: {formatDate(t.last_used_at)} · Expira: {t.expires_at ? formatDate(t.expires_at) : "nunca"}
                </Typography>
              </Box>
              <IconButton aria-label={`Revogar ${t.name}`} color="error" onClick={() => setRevoking(t)}>
                <Delete />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}

      <ConfirmModal
        open={!!revoking}
        onClose={() => setRevoking(null)}
        onConfirm={handleRevoke}
        title="Revogar token"
        btnText="Revogar"
        text={`Deseja revogar o token "${revoking?.name}"? Integrações que o usam deixarão de funcionar.`}
      />
    </Stack>
  );
}
